import * as THREE from 'three';
import { focalYIndex } from './colmapModel';
/**
 * World-to-camera rotation as a Three.js quaternion.
 *
 * COLMAP orders the components `[qw, qx, qy, qz]`; Three.js takes
 * `(x, y, z, w)`. Getting this backwards produces a rotation that still looks
 * plausible, which is exactly why it is called out here.
 */
export function worldToCameraQuaternion(qvec) {
    const [qw, qx, qy, qz] = qvec;
    return new THREE.Quaternion(qx, qy, qz, qw).normalize();
}
/** Camera centre `C = -R^T * t`. */
export function cameraCentre(image) {
    const worldToCamera = worldToCameraQuaternion(image.qvec);
    const cameraToWorld = worldToCamera.clone().invert();
    return new THREE.Vector3(image.tvec[0], image.tvec[1], image.tvec[2])
        .applyQuaternion(cameraToWorld)
        .negate();
}
export function placementFor(image) {
    return {
        position: cameraCentre(image),
        quaternion: worldToCameraQuaternion(image.qvec).invert(),
    };
}
/**
 * Viewing direction in world space - where the camera actually looks.
 * COLMAP's optical axis is camera-space +Z.
 */
export function viewDirection(image) {
    return new THREE.Vector3(0, 0, 1).applyQuaternion(placementFor(image).quaternion).normalize();
}
/**
 * Vertical field of view in degrees, for framing a look-through.
 *
 * Which slot holds `fy` depends on the model, so the lookup lives with the
 * model table (colmapModel.focalYIndex). An equirectangular camera has no
 * focal length - it covers the full sphere - and returns undefined, leaving
 * look-through on the viewer's own FOV rather than inventing one.
 */
export function verticalFovDegrees(model, params, imageHeight) {
    const index = focalYIndex(model);
    if (index === null) {
        return undefined;
    }
    const fy = params[index];
    if (!Number.isFinite(fy) || fy <= 0 || !Number.isFinite(imageHeight) || imageHeight <= 0) {
        return undefined;
    }
    return 2 * Math.atan(imageHeight / (2 * fy)) * (180 / Math.PI);
}
